<template>
  <div v-loading="loading" class="upload-data">
    <el-scrollbar class="upload-scrollbar">
      <el-form ref="formRef" :model="formData" :rules="rules">
        <!-- 基础信息 -->
        <el-card class="form-card">
          <div slot="header">基础信息</div>
          <el-row :gutter="20">
            <!-- 模版类型 -->
            <el-col :span="24">
              <el-form-item label="模版类型：" prop="type" label-width="100px">
                <el-radio-group v-model="formData.type" @change="handleTypeChange">
                  <el-radio :label="1">全本模版</el-radio>
                  <el-radio :label="2">纸张模版</el-radio>
                  <el-radio :label="3">封面模版</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <!-- 缩略图 -->
            <el-col :span="formData.type === 1 ? 24 : 12">
              <el-form-item label="缩略图（A4比例，210:297）：" prop="thumbnail">
                <div class="thumbnail-upload-container">
                  <div v-for="lang in languages" :key="lang.locale" class="thumbnail-upload-item" :class="{ 'thumbnail-upload-item-en': lang.locale === 'en' }">
                    <p class="thumbnail-lang-label"><span v-if="lang.locale === 'en'" class="required-asterisk">*</span>{{ lang.label }}</p>
                    <el-upload class="thumbnail-upload" action="" list-type="picture-card"
                      :accept="uploadImageAcceptFormat" :auto-upload="false" :show-file-list="false"
                      :on-change="(file) => handleThumbnailChange(file, lang.locale)">
                      <div v-if="getLangValue(formData.thumbnail, lang.locale)" class="thumbnail-preview-wrapper">
                        <img :src="getLangValue(formData.thumbnail, lang.locale)" class="avatar" />
                        <div class="thumbnail-remove" @click.stop="removeThumbnail(lang.locale)">
                          <i class="el-icon-close"></i>
                        </div>
                      </div>
                      <i v-else class="el-icon-plus"></i>
                    </el-upload>
                  </div>
                </div>
              </el-form-item>
            </el-col>

            <!-- 名称（仅非全本模版显示） -->
            <el-col v-if="formData.type !== 1" :span="12">
              <el-form-item prop="name" label-width="100px">
                <template slot="label">
                  <div class="custom-label">
                    <div>名称：</div>
                    <div class="field-label-hint">（xx个字内）</div>
                  </div>
                </template>
                <div class="multi-lang-field">
                  <div v-for="lang in languages" :key="lang.locale" class="lang-item" :class="{ 'lang-item-en': lang.locale === 'en' }">
                    <span class="lang-label"><span v-if="lang.locale === 'en'" class="required-asterisk">*</span>{{ lang.label }}</span>
                    <el-input :value="getLangValue(formData.name, lang.locale)"
                      @input="(val) => setLangValue(formData.name, lang.locale, val)"></el-input>
                  </div>
                </div>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 大小 -->
          <el-form-item label="大小：" prop="size" label-width="60px">
            <el-input v-model="formData.size" disabled>
              <template slot="append">MB</template>
            </el-input>
          </el-form-item>

          <!-- 页数/张数 -->
          <el-form-item :label="formData.type === 1 ? '页数：' : '张数：'" prop="pageCount" label-width="65px">
            <el-input v-model="formData.pageCount" :placeholder="`请输入${formData.type === 1 ? '页数' : '张数'}`">
              <template slot="append">{{ formData.type === 1 ? '页' : '张' }}</template>
            </el-input>
          </el-form-item>

          <!-- 规格 -->
          <el-form-item label="规格：" prop="pageType" label-width="65px">
            <el-select v-model="formData.pageType" placeholder="请选择规格" class="full-width-select">
              <el-option label="A3" value="A3"></el-option>
              <el-option label="A4" value="A4"></el-option>
              <el-option label="A5" value="A5"></el-option>
              <el-option label="B3" value="B3"></el-option>
              <el-option label="B4" value="B4"></el-option>
              <el-option label="B5" value="B5"></el-option>
              <el-option label="其他" value="other"></el-option>
            </el-select>
          </el-form-item>

          <!-- 缩略图角标 & 标题 -->
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label-width="100px">
                <template slot="label">
                  <div class="custom-label">
                    <div>缩略图角标：</div>
                    <div class="field-label-hint">（xx个字内）</div>
                  </div>
                </template>
                <div class="multi-lang-field">
                  <div v-for="lang in languages" :key="lang.locale" class="lang-item">
                    <span class="lang-label">{{ lang.label }}</span>
                    <el-input :value="getLangValue(formData.thumbnailBadge, lang.locale)"
                      @input="(val) => setLangValue(formData.thumbnailBadge, lang.locale, val)"></el-input>
                  </div>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item prop="title" label-width="100px">
                <template slot="label">
                  <div class="custom-label">
                    <div>标题：</div>
                    <div class="field-label-hint">（xx个字内）</div>
                  </div>
                </template>
                <div class="multi-lang-field">
                  <div v-for="lang in languages" :key="lang.locale" class="lang-item" :class="{ 'lang-item-en': lang.locale === 'en' }">
                    <span class="lang-label"><span v-if="lang.locale === 'en'" class="required-asterisk">*</span>{{ lang.label }}</span>
                    <el-input :value="getLangValue(formData.title, lang.locale)" :placeholder="`请输入${lang.label}标题`"
                      @input="(val) => setLangValue(formData.title, lang.locale, val)"></el-input>
                  </div>
                </div>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 适用设备 & 按钮角标 -->
          <el-row :gutter="20" class="row-margin-top">
            <el-col :span="12">
              <el-form-item label-width="100px">
                <template slot="label">
                  <div class="custom-label">
                    <div>适用设备：</div>
                    <div class="field-label-hint">（xx个字内）</div>
                  </div>
                </template>
                <div class="multi-lang-field">
                  <div v-for="lang in languages" :key="lang.locale" class="lang-item">
                    <span class="lang-label">{{ lang.label }}</span>
                    <el-input :value="getLangValue(formData.slogan, lang.locale)"
                      @input="(val) => setLangValue(formData.slogan, lang.locale, val)"></el-input>
                  </div>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label-width="100px">
                <template slot="label">
                  <div class="custom-label">
                    <div>按钮角标：</div>
                    <div class="field-label-hint">（xx个字内）</div>
                  </div>
                </template>
                <div class="multi-lang-field">
                  <div v-for="lang in languages" :key="lang.locale" class="lang-item">
                    <span class="lang-label">{{ lang.label }}</span>
                    <el-input :value="getLangValue(formData.buttonBadge, lang.locale)"
                      @input="(val) => setLangValue(formData.buttonBadge, lang.locale, val)"></el-input>
                  </div>
                </div>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 标签 & 按钮角标颜色 -->
          <el-row :gutter="20" class="row-margin-top">
            <el-col :span="12">
              <el-form-item prop="tagIds" label-width="100px">
                <template slot="label">
                  <div class="custom-label">
                    <div>标签：</div>
                    <div class="field-label-hint">（至多选择x个）</div>
                  </div>
                </template>
                <el-select v-model="formData.tagIds" multiple placeholder="请选择标签" class="full-width-select">
                  <el-option v-for="tag in tagOptions" :key="tag.id" :label="tag.name" :value="tag.id"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="按钮角标颜色：" label-width="120px">
                <div class="button-badge-color">
                  <div v-for="color in buttonBadgeColors" :key="color" class="badge-color-outer"
                    :class="{ active: formData.buttonBadgeColor === color }" @click="setButtonBadgeColor(color)">
                    <div class="badge-color-inner" :style="{ backgroundColor: color }"></div>
                  </div>
                </div>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 运营创建量 / 真实创建量 / 总创建量 -->
          <el-row :gutter="20" class="row-margin-top">
            <el-col :span="7" class="usage-count-col">
              <el-form-item label="运营创建量：" label-width="90px">
                <el-input :value="formData.usageCount" placeholder="0" @input="handleUsageCountInput"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="7" class="usage-count-col">
              <el-form-item label="真实创建量：" label-width="90px">
                <el-input :value="formData.realUsageCount" disabled></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="7">
              <el-form-item label="总创建量：" label-width="90px">
                <el-input :value="formData.totalUsageCount" disabled></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </el-card>

        <!-- 运营信息 -->
        <el-card class="form-card">
          <div slot="header">运营信息</div>
          <!-- 发布人 -->
          <el-form-item label="发布人：" prop="publisherId" label-width="100px">
            <el-select v-model="formData.publisherId" placeholder="请选择发布人" class="full-width-select"
              @change="handlePublisherChange">
              <el-option v-for="publisher in publisherOptions" :key="publisher.id" :label="publisher.name"
                :value="publisher.id"></el-option>
            </el-select>
          </el-form-item>
          <!-- 发布人摘要（只读显示） -->
          <el-form-item v-if="publisherDetails && publisherDetails.summary" label="发布人介绍：">
            <div class="publisher-summary" v-html="getLangValue(publisherDetails.summary, 'zh_CN')"></div>
          </el-form-item>

          <!-- 资料介绍（富文本编辑器） -->
          <el-form-item prop="detail" label-position="top">
            <template slot="label">
              <div>
                资料介绍：
                <span class="field-label-hint">（至多xx个字）</span>
              </div>
            </template>
            <div class="detail-editor-wrapper">
              <quill-editor ref="editor" v-model="detailContent" :options="editorOptions"
                class="detail-editor"></quill-editor>
              <div class="detail-lang-tabs">
                <span v-for="lang in languages" :key="lang.locale" class="lang-tab"
                  :class="{ active: detailLang === lang.locale }" @click="changeDetailLang(lang.locale)">
                  <span v-if="lang.locale === 'en'" class="required-asterisk">*</span>{{ lang.label }}
                </span>
              </div>
            </div>
          </el-form-item>

          <!-- 模版素材信息预览图 -->
          <el-form-item label-position="top">
            <div class="preview-header">
              <span>模版素材信息预览图：（最多上传9张）</span>
              <div class="preview-lang-tabs">
                <span v-for="lang in languages" :key="lang.locale" class="lang-tab"
                  :class="{ active: previewLang === lang.locale }" @click="changePreviewLang(lang.locale)">
                  <span v-if="lang.locale === 'en'" class="required-asterisk">*</span>{{ lang.label }}
                </span>
              </div>
            </div>
            <div class="preview-upload-section">
              <el-upload v-if="currentPreview.length < 9" ref="previewUploadRef" action="" drag
                :limit="9 - currentPreview.length" multiple :accept="uploadImageAcceptFormat" :auto-upload="false"
                :show-file-list="false" :on-change="handlePreviewChange" :on-exceed="handlePreviewExceed">
                <el-button type="primary">预览图上传</el-button>
                <div class="upload-hint">
                  可拖拽文件到此区域进行上传
                </div>
              </el-upload>
              <div v-if="currentPreview.length" class="preview-wrap">
                <div v-for="(item, index) in currentPreview" :key="index" class="preview-item">
                  <el-upload class="preview-upload" action="" :accept="uploadImageAcceptFormat" :auto-upload="false"
                    :show-file-list="false" :on-change="(file) => handlePreviewReplace(file, index)">
                    <el-image :src="item.url" fit="fill" class="preview-image" :lazy="true"></el-image>
                    <div class="preview-remove" @click.stop="removePreviewFile(index, item.url)">
                      <i class="el-icon-close"></i>
                    </div>
                  </el-upload>
                </div>
              </div>
            </div>
          </el-form-item>
        </el-card>

        <!-- 上架型号组 -->
        <el-card class="form-card">
          <div slot="header">上架型号组</div>

          <el-form-item label="发布地区：" prop="releaseAreaGroupIds" label-width="100px">
            <el-select v-model="formData.releaseAreaGroupIds" multiple placeholder="请选择发布地区" class="full-width-select">
              <el-option v-for="area in releaseAreaOptions" :key="area.id" :label="area.name"
                :value="area.id"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="型号组：" prop="modelGroupIds" label-width="100px">
            <el-select v-model="formData.modelGroupIds" multiple placeholder="请选择型号组" class="full-width-select">
              <el-option v-for="modelGroup in modelGroupOptions" :key="modelGroup.id" :label="modelGroup.name"
                :value="modelGroup.id"></el-option>
            </el-select>
          </el-form-item>
        </el-card>

        <!-- 付费信息 -->
        <el-card class="form-card">
          <div slot="header">付费信息</div>

          <el-form-item label="付费类型：" prop="paymentInfo.type" label-width="100px">
            <el-select v-model="formData.paymentInfo.type" placeholder="请选择付费类型" class="full-width-select"
              @change="handlePaymentTypeChange">
              <el-option label="免费" value="FREE"></el-option>
              <el-option label="付费" value="PREMIUM"></el-option>
              <el-option label="Pro会员领取" value="VIP_PRO"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="原价：" prop="paymentInfo.price" label-width="100px">
            <el-input v-model="formData.paymentInfo.price" placeholder="请输入原价" @input="formatPriceInput('price')">
              <template slot="append">元</template>
            </el-input>
          </el-form-item>

          <el-form-item label="常规折扣价：" prop="paymentInfo.price1" label-width="100px">
            <el-input v-model="formData.paymentInfo.price1" placeholder="请输入常规折扣价" :disabled="isPriceDisabled"
              @input="formatPriceInput('price1')">
              <template slot="append">
                <span v-if="calculateDiscount('price1')">
                  预估折扣 {{ calculateDiscount('price1') }}%
                </span>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item label="文星折扣价：" prop="paymentInfo.price2" label-width="100px">
            <el-input v-model="formData.paymentInfo.price2" placeholder="请输入文星折扣价" :disabled="isPriceDisabled"
              @input="formatPriceInput('price2')">
              <template slot="append">
                <span v-if="calculateDiscount('price2')">
                  预估折扣 {{ calculateDiscount('price2') }}%
                </span>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item label="Pro折扣价：" prop="paymentInfo.price3" label-width="100px">
            <el-input v-model="formData.paymentInfo.price3" placeholder="请输入Pro折扣价" :disabled="isPriceDisabled"
              @input="formatPriceInput('price3')">
              <template slot="append">
                <span v-if="calculateDiscount('price3')">
                  预估折扣 {{ calculateDiscount('price3') }}%
                </span>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item label="谷歌商品ID（常规折扣）：" prop="paymentInfo.googleProductId" label-width="190px">
            <el-input v-model="formData.paymentInfo.googleProductId" placeholder="请输入谷歌商品ID"
              @input="formatProductIdInput('googleProductId')">
              <template slot="append">
                <a href="https://f1ztck5lkx.feishu.cn/sheets/IlmlsE5oKhEbydtke2ccblgDnnu" target="_blank"
                  class="product-id-link">海外付费素材谷歌商品ID对照表</a>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item label="谷歌商品ID（Pro会员折扣价）：" prop="paymentInfo.googleProductId3" label-width="230px">
            <el-input v-model="formData.paymentInfo.googleProductId3" placeholder="请输入谷歌商品ID"
              @input="formatProductIdInput('googleProductId3')">
              <template slot="append">
                <a href="https://f1ztck5lkx.feishu.cn/sheets/IlmlsE5oKhEbydtke2ccblgDnnu" target="_blank"
                  class="product-id-link">海外付费素材谷歌商品ID对照表</a>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item label="苹果商品ID（常规折扣）：" prop="paymentInfo.appleProductId" label-width="190px">
            <el-input v-model="formData.paymentInfo.appleProductId" placeholder="请输入苹果商品ID"
              @input="formatProductIdInput('appleProductId')">
              <template slot="append">
                <a href="https://f1ztck5lkx.feishu.cn/wiki/TobHwTMuBi0ZTpkqmoLcwIbWnjb?sheet=b872e1" target="_blank"
                  class="product-id-link">苹果商品ID对照表</a>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item label="苹果商品ID（Pro会员折扣价）：" prop="paymentInfo.appleProductId3" label-width="230px">
            <el-input v-model="formData.paymentInfo.appleProductId3" placeholder="请输入苹果商品ID"
              @input="formatProductIdInput('appleProductId3')">
              <template slot="append">
                <a href="https://f1ztck5lkx.feishu.cn/wiki/TobHwTMuBi0ZTpkqmoLcwIbWnjb?sheet=b872e1" target="_blank"
                  class="product-id-link">苹果商品ID对照表</a>
              </template>
            </el-input>
          </el-form-item>
        </el-card>

        <!-- 附件信息 -->
        <el-card class="form-card">
          <div slot="header">
            <span v-if="formData.type === 1">全本模版附件信息（点击上传附件）</span>
            <span v-else-if="formData.type === 2">纸张模版附件信息（点击上传附件，最多32张）</span>
            <span v-else-if="formData.type === 3">封面模版附件信息（点击上传附件，最多16张）</span>
          </div>
          <div class="preview-upload-section">
            <el-upload
              v-if="formData.type === 1 || (attachUploadLimit !== null && tempAttachs.length < attachUploadLimit)"
              ref="attachUploadRef" drag action="" :accept="acceptFilter" :auto-upload="false" :show-file-list="false"
              :limit="formData.type === 1 ? null : (attachUploadLimit - tempAttachs.length)" multiple
              :on-change="handleAttachmentChange" :on-exceed="handleAttachExceed">
              <el-button type="primary">附件上传</el-button>
              <div class="upload-hint">
                可拖拽文件到此区域进行上传
              </div>
            </el-upload>
            <template v-if="tempAttachs.length">
              <!-- 全本模版：显示标签 -->
              <div v-if="formData.type === 1" class="attach-wrap attach-tags-wrap">
                <el-tag v-for="(item, index) in tempAttachs" :key="index" closable
                  @close="removeAttachFile(index, item.url)">
                  <span>{{ item.data.name }}</span>
                </el-tag>
              </div>
              <!-- 纸张/封面模版：显示图片+多语言名称输入 -->
              <div v-else class="attach-wrap">
                <div v-for="(item, index) in tempAttachs" :key="index" class="attach-item">
                  <div class="attach-image-wrapper">
                    <el-image :src="item.url" fit="fill" class="attach-image" :lazy="true"
                      :preview-src-list="tempAttachs.map(i => i.url)" :initial-index="index"></el-image>
                  </div>
                  <div class="attach-name-inputs">
                    <div v-for="nameData in item.data.displayName" :key="nameData.locale" class="attach-name-row">
                      <p class="attach-name-label">{{ getLangLabel(nameData.locale) }}</p>
                      <el-input v-model="nameData.value" class="attach-name-input" clearable></el-input>
                    </div>
                  </div>
                  <div class="attach-remove" @click="removeAttachFile(index, item.url)">
                    <i class="el-icon-close"></i>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </el-card>

        <!-- 关联信息 -->
        <el-card class="form-card" v-if="formData.categories && formData.categories.length">
          <div slot="header">关联信息</div>
          <el-table :data="formData.categories" border size="small" class="full-width-table">
            <el-table-column type="index" label="序号" width="60"></el-table-column>
            <el-table-column prop="id" label="分类ID"></el-table-column>
            <el-table-column prop="name" label="分类名称"></el-table-column>
          </el-table>
        </el-card>

        <!-- 底部按钮 -->
        <div class="footer-buttons">
          <el-button v-if="detailId" type="primary" @click="copyFormData">复制一份</el-button>
          <el-button type="primary" @click="handleSubmit">确认</el-button>
        </div>
      </el-form>
    </el-scrollbar>
  </div>
</template>

<script>
import api from "@/api";
import { valueByLanguage, getDefaultLanguageData, getDefaultFileLanguageData } from "@/utils/college";
import {
  getMd5,
  autoConvertSize,
  hasSameName,
  updatePrices,
  checkLanguageData,
  uploadFileToOss,
  signatureUrl
} from "@/utils/uploadUtils";
import { cloneDeep } from "lodash";
import Sortable from "sortablejs";
import { quillEditor } from "vue-quill-editor";
import { mapGetters } from "vuex";

// 对齐galaxy：定义OSSApiKey常量
const OSSApiKey = {
  Common: "common",
  Message: "message"
};

export default {
  name: "UploadData",
  components: {
    quillEditor
  },
  props: {
    detailId: {
      type: Number,
      default: null
    }
  },
  computed: {
    ...mapGetters(["buckets", "bucketData"]), // 使用新的bucketData getter（对齐galaxy）
    // 当前预览图列表（根据当前语言）
    currentPreview: {
      get() {
        return this.tempPerviews[this.previewLang] || [];
      },
      set(val) {
        this.$set(this.tempPerviews, this.previewLang, val);
      }
    },
    // 附件总大小
    attachsSize() {
      return this.tempAttachs.reduce((acc, curr) => {
        return acc + (curr.data ? curr.data.size : 0);
      }, 0);
    },
    // 接受图片格式
    uploadImageAcceptFormat() {
      return ".png, .jpg, .bmp, .jpeg, .svg";
    },
    // 接受文件格式
    uploadFileAcceptFormat() {
      return ".pdf, .note";
    },
    // 附件接受格式（根据模版类型）
    acceptFilter() {
      if (this.formData.type === 1) {
        return this.uploadFileAcceptFormat;
      } else {
        return this.uploadImageAcceptFormat;
      }
    },
    // 附件上传限制
    attachUploadLimit() {
      if (this.formData.type === 2) {
        return 32;
      } else if (this.formData.type === 3) {
        return 16;
      } else {
        return null;
      }
    },
    // 当前详情语言的内容
    detailContent: {
      get() {
        return this.getLangValue(this.formData.detail, this.detailLang);
      },
      set(val) {
        this.setLangValue(this.formData.detail, this.detailLang, val);
      }
    },
    // 价格输入是否禁用
    isPriceDisabled() {
      return (
        this.formData.paymentInfo.type === "FREE" ||
        this.formData.paymentInfo.type === "VIP_PRO"
      );
    },
    // 编辑器配置
    editorOptions() {
      return {
        theme: "snow",
        placeholder: "请输入内容",
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            ["clean"]
          ]
        }
      };
    },
    // 表单验证规则（整体校验，参考 galaxy）
    rules() {
      return {
        thumbnail: [
          {
            required: true,
            validator: (rule, value, callback) => {
              const en = this.getLangValue(this.formData.thumbnail, "en");
              if (!en || !en.trim()) {
                callback(new Error("请上传缩略图（英文）"));
              } else {
                callback();
              }
            },
            trigger: "change",
          }
        ],
        pageType: [{ required: true, message: "请选择规格", trigger: "change" }],
        title: [
          {
            required: true,
            validator: (rule, value, callback) => {
              const en = this.getLangValue(this.formData.title, "en");
              if (!en || !en.trim()) {
                callback(new Error("请填写标题（英文）"));
              } else {
                callback();
              }
            },
            trigger: "blur"
          }
        ],
        detail: [
          {
            required: true,
            validator: (rule, value, callback) => {
              const en = this.getLangValue(this.formData.detail, "en");
              if (!en || !en.trim()) {
                callback(new Error("请填写资料介绍（英文）"));
              } else {
                callback();
              }
            },
            trigger: "blur"
          }
        ],
        modelGroupIds: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (!value || !Array.isArray(value) || value.length === 0) {
                callback(new Error("请选择上架型号组"));
              } else {
                callback();
              }
            },
            trigger: "change"
          }
        ],
        releaseAreaGroupIds: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (!value || !Array.isArray(value) || value.length === 0) {
                callback(new Error("请选择发布地区"));
              } else {
                callback();
              }
            },
            trigger: "change"
          }
        ],
        channels: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (!value || !Array.isArray(value) || value.length === 0) {
                callback(new Error("请选择投放渠道"));
              } else {
                callback();
              }
            },
            trigger: "change"
          }
        ],
        publisherId: [
          { required: true, message: "请选择发布人", trigger: "change" }
        ],
        name: this.formData.type !== 1
          ? [
            {
              required: true,
              validator: (rule, value, callback) => {
                const en = this.getLangValue(this.formData.name, "en");
                if (!en || !en.trim()) {
                  callback(new Error("请填写名称（英文）"));
                } else {
                  callback();
                }
              },
              trigger: "blur"
            }
          ]
          : null,
        pageCount: [
          { required: true, message: "纸张不能为空", trigger: "blur" }
        ],
        "paymentInfo.price": [
          {
            required: true,
            validator: (rule, value, callback) => {
              const paymentInfo = this.formData.paymentInfo;
              const max = Math.max(
                paymentInfo.price1 || 0,
                paymentInfo.price2 || 0,
                paymentInfo.price3 || 0
              );
              if (value < max && max !== 0) {
                callback(new Error("原价不能低于折扣价"));
              } else if (!value && value !== 0) {
                callback(new Error("请填写原价"));
              } else {
                callback();
              }
            },
            trigger: ["blur", "change"]
          }
        ]
      };
    }
  },
  data() {
    return {
      loading: false,
      // 与 galaxy 保持一致的语言编码
      languages: [
        { label: "英文", locale: "en" },
        { label: "简体", locale: "zh_CN" },
        { label: "繁体", locale: "zh_TW" },
      ],
      buttonBadgeColors: ["#77B1D6", "#E47F7A", "#E7C490"],
      detailLang: "en",
      previewLang: "en",
      revokeUrls: [],
      attachSortableInstance: null,
      proviewSortableInstance: null,
      publisherDetails: null,
      releaseAreaOptions: [],
      modelGroupOptions: [],
      formData: {
        type: 1,
        size: 0,
        pageCount: null,
        pageType: "",
        // 多语言字段：与 galaxy-admin 的 LanguageData 结构保持一致
        name: getDefaultLanguageData(),
        thumbnail: getDefaultLanguageData(),
        thumbnailBadge: getDefaultLanguageData(),
        title: getDefaultLanguageData(),
        slogan: getDefaultLanguageData(),
        buttonBadge: getDefaultLanguageData(),
        buttonBadgeColor: "#77B1D6",
        tagIds: [],
        publisherId: undefined,
        detail: getDefaultLanguageData(),
        previews: getDefaultFileLanguageData(),
        releaseAreaGroupIds: [],
        channels: [],
        usageCount: 0,
        realUsageCount: 0,
        totalUsageCount: 0,
        categories: [],
        attachments: [],
        paymentInfo: {
          type: "FREE",
          price: 0,
          price1: null,
          price2: null,
          price3: null,
          googleProductId: "",
          googleProductId3: "",
          appleProductId: "",
          appleProductId3: ""
        }
      },
      tempThumbnail: [],
      tempAttachs: [],
      tempPerviews: {
        zh_CN: [],
        zh_TW: [],
        en: []
      },
      tagOptions: [],
      publisherOptions: []
    };
  },
  watch: {
    detailId: {
      immediate: false,
      async handler(newVal, oldVal) {
        // 当 detailId 从 null 变为有值时，加载数据
        if (newVal && newVal !== oldVal) {
          // 确保选项数据已加载
          if (this.tagOptions.length === 0 || this.publisherOptions.length === 0) {
            await Promise.all([
              this.loadTagOptions(),
              this.loadPublisherOptions(),
              this.loadModelGroupOptions(),
              this.loadReleaseAreaOptions()
            ]);
          }
          await this.getDetail();
        } else if (!newVal && oldVal) {
          // 当 detailId 被清空时，重置表单数据
          this.resetFormData();
        }
      }
    }
  },
  mounted() {
    this.loadTagOptions();
    this.loadPublisherOptions();
    this.loadModelGroupOptions();
    this.loadReleaseAreaOptions();
    if (this.detailId) {
      this.getDetail();
    }
  },
  beforeDestroy() {
    this.removeRevokedUrl();
    this.removePreviewSortable();
    this.removeAttachSortable();
  },
  methods: {
    // 模版类型变更处理
    handleTypeChange() {
      // 清空预览图和附件
      this.tempPerviews = {
        zh_CN: [],
        zh_TW: [],
        en: []
      };
      this.tempAttachs = [];
      // 重置名称字段
      this.formData.name = this.formData.name.map(item => ({
        locale: item.locale,
        value: ""
      }));
      // 清理blob URL
      this.removeRevokedUrl();
    },
    // 多语言工具方法
    getLangValue(list, locale) {
      // 返回字符串值用于显示和输入绑定
      return valueByLanguage(list, locale, true);
    },
    setLangValue(list, locale, val) {
      if (!Array.isArray(list)) return;
      let item = list.find((it) => it.locale === locale);
      if (!item) {
        item = { locale, value: val || "" };
        list.push(item);
      } else {
        item.value = val;
      }
    },
    normalizeLanguageField(fieldName, source) {
      const src = source && Array.isArray(source) ? source : [];
      const target = getDefaultLanguageData();
      this.languages.forEach((lang) => {
        const exist = src.find((it) => it.locale === lang.locale);
        const targetItem = target.find((it) => it.locale === lang.locale);
        if (targetItem) {
          targetItem.value = exist ? exist.value : "";
        }
      });
      this.formData[fieldName] = target;
    },

    loadTagOptions() {
      api
        .getMaterialTagList({ page: 1, pageSize: 1000 })
        .then((res) => {
          this.tagOptions =
            res.data.items.map((item) => ({
              id: item.id,
              name: valueByLanguage(item.name, "zh_CN", true) || ""
            })) || [];
        });
    },
    loadPublisherOptions() {
      api
        .getMaterialPublisherList({ page: 1, pageSize: 1000 })
        .then((res) => {
          this.publisherOptions =
            res.data.items.map((item) => ({
              id: item.id,
              name: valueByLanguage(item.name, "zh_CN", true) || ""
            })) || [];
        });
    },
    loadReleaseAreaOptions() {
      api
        .getMaterialPublisherArea({ page: 1, pageSize: 1000 })
        .then((res) => {
          this.releaseAreaOptions = res.data.items || [];
          if (
            !this.detailId &&
            Array.isArray(this.releaseAreaOptions) &&
            this.releaseAreaOptions.length > 0 &&
            Array.isArray(this.formData.releaseAreaGroupIds) &&
            this.formData.releaseAreaGroupIds.length === 0
          ) {
            this.formData.releaseAreaGroupIds = this.releaseAreaOptions.map((item) => item.id);
          }
        });
    },
    loadModelGroupOptions() {
      api
      .getCollegeModelGroups({ page: 1, pageSize: 1000 })
      .then((res) => {
        this.modelGroupOptions = res.data.items || [];
      });
    },
    async handlePublisherChange() {
      // 发布人变更处理
      if (this.formData.publisherId) {
        // 从 API 获取完整的发布人详情（包括 summary）
        try {
          const res = await api.getMaterialPublisherDetails(this.formData.publisherId);
          this.publisherDetails = res.data || null;
        } catch (err) {
          console.error("获取发布人详情失败:", err);
          // 如果获取失败，尝试从选项中找到
          const publisher = this.publisherOptions.find(
            (p) => p.id === this.formData.publisherId
          );
          this.publisherDetails = publisher || null;
        }
      } else {
        this.publisherDetails = null;
      }
    },
    getLangLabel(locale) {
      const lang = this.languages.find((l) => l.locale === locale);
      return lang ? lang.label : locale;
    },
    async getDetail() {
      this.loading = true;
      try {
        const res = await api.getTemplateDetails(this.detailId);
        const data = res.data || {};

        // 基础字段（直接复制所有字段，与galaxy保持一致）
        Object.keys(data).forEach((key) => {
          if (this.formData.hasOwnProperty(key) && key !== "paymentInfo") {
            this.formData[key] = data[key];
          }
        });

        // 价格转换（分转元）
        if (data.paymentInfo) {
          this.formData.paymentInfo = { ...data.paymentInfo };
          updatePrices(this.formData.paymentInfo, "get");
        }

        // 标签和型号组
        this.formData.tagIds = data.tags ? data.tags.map((tag) => tag.id) : [];
        this.formData.modelGroupIds = data.modelGroups  
          ? data.modelGroups.map((modelGroup) => modelGroup.id)
          : [];
        this.formData.releaseAreaGroupIds = data.releaseAreaGroups
          ? data.releaseAreaGroups.map((area) => area.id)
          : [];

        // 发布人详情（从 API 获取完整信息，包括 summary）
        if (data.publisherId) {
          api
            .getMaterialPublisherDetails(data.publisherId)
            .then((res) => {
              this.publisherDetails = res.data || null;
            })
            .catch((err) => {
              console.error("获取发布人详情失败:", err);
              // 如果获取失败，尝试从选项中找到
              const publisher = this.publisherOptions.find(
                (p) => p.id === data.publisherId
              );
              this.publisherDetails = publisher || null;
            });
        }

        // 规范化多语言字段
        this.normalizeLanguageField("name", data.name);
        this.normalizeLanguageField("thumbnail", data.thumbnail);
        this.normalizeLanguageField("thumbnailBadge", data.thumbnailBadge);
        this.normalizeLanguageField("title", data.title);
        this.normalizeLanguageField("slogan", data.slogan);
        this.normalizeLanguageField("buttonBadge", data.buttonBadge);
        this.normalizeLanguageField("detail", data.detail);

        // 处理预览图（多语言）
        if (data.previews && Array.isArray(data.previews)) {
          // 使用 Promise.all 等待所有预览图的 URL 生成完成
          await Promise.all(
            data.previews.map(async (previewItem) => {
              const { locale, value } = previewItem;
              if (!value || !Array.isArray(value) || value.length === 0) {
                this.$set(this.tempPerviews, locale, []);
                return;
              }

              const result = await Promise.all(
                value.map(async (item) => {
                  try {
                    // 如果后端已经下发了完整 URL，则直接使用，避免重复签名
                    if (item.url && (item.url.startsWith("http://") || item.url.startsWith("https://"))) {
                      return { url: item.url, data: item };
                    }

                    // 对齐 galaxy-admin：直接使用后端给的 bucket，不写死默认值
                    const signedUrl = await signatureUrl({
                      key: item.key || item.url || "",
                      bucket: item.bucket
                    });

                    return { url: signedUrl || "", data: item };
                  } catch (error) {
                    console.error(`生成预览图 URL 失败 (${item.key || item.url}):`, error);
                    return { url: "", data: item };
                  }
                })
              );

              // 使用 $set 确保 Vue 2 的响应式更新
              this.$set(this.tempPerviews, locale, result);
            })
          );
        }

        // 处理附件
        if (this.formData.type !== 1) {
          // 纸张/封面模版：需要显示图片和多语言名称
          this.tempAttachs = await Promise.all(
            (data.attachments || []).map(async (item) => {
              // 优先使用已有的 url，如果没有则通过 signatureUrl 生成
              let url = item.url;
              if (!url && item.key) {
                // 对齐 galaxy-admin：直接使用后端给的 bucket，不写死默认值
                url = await signatureUrl({ key: item.key, bucket: item.bucket });
              }
              return { url: url || "", data: item };
            })
          );
        } else {
          // 全本模版：只需要显示标签
          this.tempAttachs = (data.attachments || []).map((item) => ({
            data: item
          }));
        }
        this.formData.size = autoConvertSize(this.attachsSize, "B", "MB");

        // 检查多语言数据
        checkLanguageData(this.formData, [
          { field: "name" },
          { field: "thumbnail" },
          { field: "thumbnailBadge" },
          { field: "title" },
          { field: "slogan" },
          { field: "buttonBadge" },
          { field: "detail" },
          {
            field: "attachments",
            skip: this.formData.type === 1,
            nestedField: "displayName"
          },
          { field: "previews", isArrayValue: true }
        ], "add");

        // 初始化拖拽排序
        this.$nextTick(() => {
          this.initPreviewSortable();
          this.initAttachSortable();
        });

        this.loading = false;
      } catch (err) {
        this.$message.error(err.message || "获取详情失败");
        this.loading = false;
      }
    },
    handleThumbnailChange(file, locale) {
      const currentValue = this.getLangValue(this.formData.thumbnail, locale);
      if (currentValue && currentValue.startsWith("blob:")) {
        this.removeRevokedUrl(currentValue);
      }

      const url = URL.createObjectURL(file.raw);
      this.setLangValue(this.formData.thumbnail, locale, url);
      this.revokeUrls.push(url);

      const existingIndex = this.tempThumbnail.findIndex(
        (item) => item.locale === locale
      );
      if (existingIndex !== -1) {
        this.tempThumbnail[existingIndex].file = file.raw;
      } else {
        this.tempThumbnail.push({ locale, file: file.raw });
      }
    },
    removeThumbnail(locale) {
      const currentValue = this.getLangValue(this.formData.thumbnail, locale);
      if (currentValue && currentValue.startsWith("blob:")) {
        this.removeRevokedUrl(currentValue);
      }
      this.setLangValue(this.formData.thumbnail, locale, "");

      const existingIndex = this.tempThumbnail.findIndex(
        (item) => item.locale === locale
      );
      if (existingIndex !== -1) {
        this.tempThumbnail.splice(existingIndex, 1);
      }
    },
    // 预览图相关
    changePreviewLang(lang) {
      this.previewLang = lang;
      if (this.$refs.previewUploadRef) {
        this.$refs.previewUploadRef.clearFiles();
      }
      this.$nextTick(() => {
        this.initPreviewSortable();
      });
    },
    async handlePreviewChange(file) {
      const format = file.raw.name.split(".").pop();
      if (!this.checkAttachAccept(format, this.uploadImageAcceptFormat)) return;

      const fileData = await this.getFileData(file, this.currentPreview);
      if (fileData) {
        this.currentPreview = [...this.currentPreview, fileData];
        if (this.$refs.previewUploadRef) {
          this.$refs.previewUploadRef.clearFiles();
        }
        this.$nextTick(() => {
          this.initPreviewSortable();
        });
      }
    },
    handlePreviewExceed() {
      this.$message.error("最多只能上传9张");
    },
    async removePreviewFile(index, url) {
      try {
        await this.$confirm("是否确认要删除？", "提示", {
          type: "warning"
        });
        this.removeRevokedUrl(url);
        this.currentPreview.splice(index, 1);
        if (this.$refs.previewUploadRef) {
          this.$refs.previewUploadRef.clearFiles();
        }
        if (!this.currentPreview.length) {
          this.removePreviewSortable();
        }
      } catch (e) {
        // 用户取消
      }
    },
    async handlePreviewReplace(file, index) {
      const format = file.raw.name.split(".").pop();
      if (!this.checkAttachAccept(format, this.uploadImageAcceptFormat)) return;

      const oldItem = this.currentPreview[index];
      if (oldItem && oldItem.url) {
        this.removeRevokedUrl(oldItem.url);
      }

      const url = URL.createObjectURL(file.raw);
      const fileData = await this.getFileData(file, this.currentPreview);
      if (fileData) {
        this.currentPreview.splice(index, 1, fileData);
        this.revokeUrls.push(url);
      }
    },
    async initPreviewSortable() {
      await this.$nextTick();
      if (this.proviewSortableInstance) {
        this.removePreviewSortable();
      }
      const wrapper = document.querySelector(".preview-wrap");
      if (!wrapper) return;

      this.proviewSortableInstance = Sortable.create(wrapper, {
        animation: 150,
        onEnd: async ({ newIndex, oldIndex }) => {
          const currentNode = this.currentPreview.splice(oldIndex, 1)[0];
          this.currentPreview.splice(newIndex, 0, currentNode);
          const newArray = [...this.currentPreview];
          this.currentPreview = [];
          await this.$nextTick();
          this.currentPreview = newArray;
          this.initPreviewSortable();
        }
      });
    },
    removePreviewSortable() {
      if (this.proviewSortableInstance) {
        this.proviewSortableInstance.destroy();
        this.proviewSortableInstance = null;
      }
    },
    // 附件相关
    async getFileData(file, arr, hasDisplayName = false) {
      const url = URL.createObjectURL(file.raw);
      const result = (arr || []).map((item) => (item.data ? item.data : item));
      await this.$store.dispatch("getBucketData").catch(() => {});
      // 对齐galaxy：使用common配置获取bucket（动态获取，不写死）
      // galaxy使用：bucketData[OSSApiKey.Common]?.bucket，其中OSSApiKey.Common = "common"
      const commonBucketData = this.bucketData && this.bucketData[OSSApiKey.Common];
      const bucket = commonBucketData && commonBucketData.bucket;

      if (!bucket) {
        this.$message.error(`bucket lost：${commonBucketData}`);
        return;
      }

      const params = {
        file: file.raw,
        url,
        data: {
          name: hasSameName(result, file.raw.name),
          format: file.raw.name.split(".").pop(),
          key: "",
          size: file.raw.size,
          bucket,
          md5: ""
        }
      };

      if (hasDisplayName) {
        params.data.displayName = getDefaultLanguageData();
      }

      this.revokeUrls.push(url);
      return params;
    },
    checkAttachAccept(format, acceptFormats) {
      const formats = acceptFormats || this.uploadImageAcceptFormat;
      const isPass = formats.includes(`.${format.toLowerCase()}`);
      if (!isPass) {
        this.$message.error(`只支持${formats}`);
      }
      return isPass;
    },
    async handleAttachmentChange(file) {
      const format = file.raw.name.split(".").pop();

      if (!this.checkAttachAccept(format, this.acceptFilter)) return;

      const fileData = await this.getFileData(
        file,
        this.tempAttachs,
        this.formData.type !== 1
      );
      if (fileData) {
        this.tempAttachs = [...this.tempAttachs, fileData];
        this.formData.size = autoConvertSize(this.attachsSize, "B", "MB");
        if (this.$refs.attachUploadRef) {
          this.$refs.attachUploadRef.clearFiles();
        }
        this.$nextTick(() => {
          this.initAttachSortable();
        });
      }
    },
    handleAttachExceed() {
      const limit = this.attachUploadLimit;
      if (limit) {
        this.$message.error(`最多只能上传${limit}张`);
      }
    },
    async removeAttachFile(index, url) {
      try {
        await this.$confirm("是否确认要删除？", "提示", {
          type: "warning"
        });
        if (url) {
          this.removeRevokedUrl(url);
        }
        this.tempAttachs.splice(index, 1);
        if (this.$refs.attachUploadRef) {
          this.$refs.attachUploadRef.clearFiles();
        }
        this.formData.size = autoConvertSize(this.attachsSize, "B", "MB");
        if (!this.tempAttachs.length) {
          this.removeAttachSortable();
        }
      } catch (e) {
        // 用户取消
      }
    },
    async initAttachSortable() {
      await this.$nextTick();
      if (this.attachSortableInstance) {
        this.removeAttachSortable();
      }
      const wrapper = document.querySelector(".attach-wrap");
      if (!wrapper) return;

      this.attachSortableInstance = Sortable.create(wrapper, {
        animation: 150,
        filter: ".el-input",
        preventOnFilter: false,
        onEnd: async ({ newIndex, oldIndex }) => {
          const currentNode = this.tempAttachs.splice(oldIndex, 1)[0];
          this.tempAttachs.splice(newIndex, 0, currentNode);
          const newArray = [...this.tempAttachs];
          this.tempAttachs = [];
          await this.$nextTick();
          this.tempAttachs = newArray;
          this.initAttachSortable();
        }
      });
    },
    removeAttachSortable() {
      if (this.attachSortableInstance) {
        this.attachSortableInstance.destroy();
        this.attachSortableInstance = null;
      }
    },
    // 富文本编辑器相关
    changeDetailLang(lang) {
      this.detailLang = lang;
      this.$nextTick(() => {
        if (this.$refs.editor && this.$refs.editor.quill) {
          const content = this.getLangValue(this.formData.detail, lang);
          this.$refs.editor.quill.root.innerHTML = content || "";
        }
      });
    },
    setButtonBadgeColor(color) {
      this.formData.buttonBadgeColor = color;
    },
    // 运营创建量相关
    handleUsageCountInput(value) {
      if (value === null || value === undefined) {
        this.formData.usageCount = 0;
      } else {
        let formattedValue = String(value).replace(/[^\d]/g, "");
        if (formattedValue.length > 1 && formattedValue[0] === "0") {
          formattedValue = formattedValue.substring(1);
        }
        this.formData.usageCount =
          formattedValue === "" ? 0 : parseInt(formattedValue, 10) || 0;
      }
      const real = this.formData.realUsageCount || 0;
      const usage = this.formData.usageCount || 0;
      this.formData.totalUsageCount = real + usage;
    },
    // 付费信息相关
    handlePaymentTypeChange(value) {
      if (value === "VIP_PRO" || value === "FREE") {
        const filter = {
          VIP_PRO: ["type", "price"],
          FREE: ["type", "price"]
        };
        Object.keys(this.formData.paymentInfo).forEach((key) => {
          if (!filter[value].includes(key)) {
            this.formData.paymentInfo[key] = null;
          }
        });
      }
    },
    formatPriceInput(field) {
      let value = this.formData.paymentInfo[field];
      if (value === null || value === undefined) return;
      value = String(value).replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");
      if (
        (value.length > 1 && value[0] === "0" && value[1] !== ".") ||
        value[0] === "."
      ) {
        value = value.substring(1);
      }
      this.formData.paymentInfo[field] = value === "" ? null : parseFloat(value) || null;
    },
    formatProductId(field) {
      this.formData.paymentInfo[field] = String(
        this.formData.paymentInfo[field] || ""
      ).replace(/\s/g, "");
    },
    formatProductIdInput(field) {
      this.formatProductId(field);
    },
    calculateDiscount(field) {
      const price = this.formData.paymentInfo[field];
      const originalPrice = this.formData.paymentInfo.price;
      if (price && originalPrice) {
        const discount =
          ((originalPrice - price) / originalPrice) * 100;
        return Math.round(discount);
      }
      return 0;
    },
    // 校验商品ID对应价格
    checkProductId() {
      const paymentInfo = this.formData.paymentInfo;
      const googleProductId = paymentInfo.googleProductId;
      const googleProductId3 = paymentInfo.googleProductId3;
      const appleProductId = paymentInfo.appleProductId;
      const appleProductId3 = paymentInfo.appleProductId3;
      const price1 = paymentInfo.price1;
      const price3 = paymentInfo.price3;

      if ((googleProductId || appleProductId) && !price1) {
        this.$message.warning("请输入对应商品ID的常规折扣价");
        return false;
      }

      if ((googleProductId3 || appleProductId3) && !price3) {
        this.$message.warning("请输入对应商品ID的Pro折扣价");
        return false;
      }

      return true;
    },
    // 工具方法
    removeRevokedUrl(url) {
      if (url) {
        URL.revokeObjectURL(url);
        const idx = this.revokeUrls.findIndex((item) => item === url);
        if (idx !== -1) {
          this.revokeUrls.splice(idx, 1);
        }
        return;
      }
      for (let url of this.revokeUrls) {
        URL.revokeObjectURL(url);
      }
      this.revokeUrls = [];
    },
    resetFormData() {
      // 重置表单数据到初始状态
      this.formData = {
        type: 1,
        size: 0,
        pageCount: null,
        pageType: "",
        name: getDefaultLanguageData(),
        thumbnail: getDefaultLanguageData(),
        thumbnailBadge: getDefaultLanguageData(),
        title: getDefaultLanguageData(),
        slogan: getDefaultLanguageData(),
        buttonBadge: getDefaultLanguageData(),
        buttonBadgeColor: "#77B1D6",
        tagIds: [],
        publisherId: undefined,
        detail: getDefaultLanguageData(),
        previews: getDefaultFileLanguageData(),
        modelGroupIds: [],
        releaseAreaGroupIds: [],
        channels: [],
        usageCount: 0,
        realUsageCount: 0,
        totalUsageCount: 0,
        categories: [],
        attachments: [],
        paymentInfo: {
          type: "FREE",
          price: 0,
          price1: null,
          price2: null,
          price3: null,
          googleProductId: "",
          googleProductId3: "",
          appleProductId: "",
          appleProductId3: ""
        }
      };

      // 清空临时文件
      this.tempThumbnail = [];
      this.tempAttachs = [];
      this.tempPerviews = {
        zh_CN: [],
        zh_TW: [],
        en: []
      };

      // 清空发布人详情
      this.publisherDetails = null;

      // 清理所有 blob URL
      this.removeRevokedUrl();

      // 移除拖拽排序实例
      this.removePreviewSortable();
      this.removeAttachSortable();

      // 重置表单验证状态
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.clearValidate();
        }
      });
    },
    getCategoryName(category) {
      if (!category || !category.name) return "";
      if (Array.isArray(category.name)) {
        return valueByLanguage(category.name, "zh_CN", true) || "";
      }
      return category.name;
    },
    formatFormData() {
      // 转换附件大小为 KB
      this.formData.size = autoConvertSize(this.attachsSize, "B", "KB");
      this.formData.pageCount = +this.formData.pageCount;

      // 格式化预览图数据
      this.formData.previews = Object.entries(this.tempPerviews).map(
        ([lang, items]) => ({
          locale: lang,
          value: items.map((item) => item.data)
        })
      );

      // 格式化附件数据
      this.formData.attachments = this.tempAttachs.map((item) => item.data);

      // 删除空的多语言数据
      checkLanguageData(this.formData, [
        { field: "name" },
        { field: "thumbnail" },
        { field: "thumbnailBadge" },
        { field: "title" },
        { field: "slogan" },
        { field: "buttonBadge" },
        { field: "detail" },
        {
          field: "attachments",
          skip: this.formData.type === 1,
          nestedField: "displayName"
        },
        { field: "previews", isArrayValue: true }
      ], "del");

      return { ...this.formData };
    },
    async copyFormData() {
      try {
        this.loading = true;
        await api.copyTemplate(this.detailId);
        this.$message.success("复制成功");
        this.loading = false;
      } catch (error) {
        this.loading = false;
        if (error && typeof error === 'object' && Object.keys(error).length) {
          const key = Object.keys(error)[0];
          const errorMsg = error[key] && error[key][0] ? error[key][0].message : (error.message || "复制失败");
          this.$message.warning(errorMsg);
        } else {
          this.$message.error(error.message || "复制失败");
        }
      }
    },
    hasEmptyDisplayName() {
      return this.tempAttachs.some(item => {
        if (!item.data.displayName) return false;
        return item.data.displayName.some(name => !name.value.trim());
      });
    },
    async uploadFiles() {
      try {
        if (!this.tempPerviews.en || !this.tempPerviews.en.length) {
          this.$message.warning("请上传英文预览图");
          return false;
        }

        if (!this.tempAttachs.length) {
          this.$message.warning("请上传附件");
          return false;
        }

        if (this.hasEmptyDisplayName()) {
          this.$message.warning("附件名称不能为空");
          return false;
        }

        const uploadTasks = [
          this.tempThumbnail.length && this.uploadThumbnail(),
          this.uploadPreview(),
          this.tempAttachs.length && this.uploadAttachment()
        ].filter(Boolean);

        await Promise.all(uploadTasks);
        return true;
      } catch (error) {
        this.$message.error(`上传文件失败：${error.message || error}`);
        return false;
      }
    },
    async uploadThumbnail() {
      const uploadPromises = this.tempThumbnail
        .filter((item) => item.file)
        .map(async (item) => {
          const { file, locale } = item;
          const url = await uploadFileToOss({
            file,
            dir: "res_template/thumbnails/"
          });
          this.setLangValue(this.formData.thumbnail, locale, url);
        });
      await Promise.all(uploadPromises);
    },
    async uploadPreview() {
      const uploadPromises = Object.entries(this.tempPerviews).flatMap(
        ([lang, items]) =>
          items
            .filter((item) => item.file)
            .map(async (item) => {
              const { data, file } = item;
              const md5 = await getMd5(file);
              data.md5 = md5;

              const url = await uploadFileToOss({
                file,
                dir: "res_template/preview/"
              });
              if (url) {
                const index = url.indexOf("res_template/preview/");
                data.key = index !== -1 ? url.substring(index) : url;
              }
            })
      );
      await Promise.all(uploadPromises);
    },
    async uploadAttachment() {
      const uploadPromises = this.tempAttachs
        .filter((item) => item.file)
        .map(async (item) => {
          const { data, file } = item;
          const md5 = await getMd5(file);
          data.md5 = md5;

          const url = await uploadFileToOss({
            file,
            dir: "res_template/attachments/"
          });
          if (url) {
            const index = url.indexOf("res_template/attachments/");
            data.key = index !== -1 ? url.substring(index) : url;
          }
        });
      await Promise.all(uploadPromises);
    },
    async handleSubmit() {
      // Element UI 表单校验
      try {
        await this.$refs.formRef.validate();
      } catch (fields) {
        // fields 是校验失败的字段信息，取第一条错误消息弹出提示
        if (fields && typeof fields === "object") {
          var firstKey = Object.keys(fields)[0];
          var firstMsg = fields[firstKey] && fields[firstKey][0] && fields[firstKey][0].message;
          if (firstMsg) {
            this.$message.error(firstMsg);
          }
        }
        return;
      }

      try {
        this.loading = true;

        // 校验商品ID
        if (!this.checkProductId()) {
          this.loading = false;
          return;
        }

        // 上传文件
        if (!(await this.uploadFiles())) {
          this.loading = false;
          return;
        }

        // 格式化表单数据
        var params = cloneDeep(this.formatFormData());

        // 价格转换（元转分）
        updatePrices(params.paymentInfo, "update");

        // 提交
        if (this.detailId) {
          await api.updateTemplate(this.detailId, params);
        } else {
          await api.addTemplate(params);
        }

        this.$message.success("操作成功");
        this.loading = false;
        this.removeRevokedUrl();
        this.$emit("success");
      } catch (error) {
        console.log(error);
        var errorMsg = typeof error === 'string' ? error : (error.message || "保存失败");
        this.$message.error("保存失败：" + errorMsg);
        // 补充缺少的语言数据
        checkLanguageData(this.formData, [
          { field: "name" },
          { field: "thumbnail" },
          { field: "thumbnailBadge" },
          { field: "title" },
          { field: "slogan" },
          { field: "buttonBadge" },
          { field: "detail" },
          {
            field: "attachments",
            skip: this.formData.type === 1,
            nestedField: "displayName"
          },
          { field: "previews", isArrayValue: true }
        ], "add");
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.upload-data {
  /* padding: 20px; */
  background-color: #fff;
  display: flex;
  width: 100%;
  height: calc(100vh - 82px);
}

.upload-scrollbar {
  flex: 1;
  padding: 16px;
}

.form-card {
  margin-bottom: 16px;
}

.upload-hint {
  font-size: 14px;
  margin-top: 8px;
  color: #9ca3af;
}

.thumbnail-upload .el-upload {
  width: 106px;
  height: 106px;
}

.avatar {
  width: 100%;
  height: 100%;
  display: block;
}

.thumbnail-preview-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.thumbnail-remove {
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0 0 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 14px;
}

.thumbnail-remove:hover {
  background: rgba(0, 0, 0, 0.7);
}

.thumbnail-upload-container {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.thumbnail-upload-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.thumbnail-lang-label {
  margin-top: 8px;
  font-size: 12px;
  color: #666;
  text-align: center;
}

.required-asterisk {
  color: #f56c6c;
  margin-right: 2px;
}

:deep(.el-form-item.is-error) .lang-item:not(.lang-item-en) .el-input__inner {
  border-color: #dcdfe6;
}

:deep(.el-form-item.is-error) .lang-item:not(.lang-item-en) .el-input__inner:focus {
  border-color: #409eff;
}

:deep(.el-form-item.is-error) .thumbnail-upload-item:not(.thumbnail-upload-item-en) .el-upload--picture-card {
  border-color: #c0ccda;
}

.custom-label {
  display: flex;
  flex-direction: column;
  text-align: left;
  line-height: 1.5;
  padding-top: 5px;
}

:deep(.el-form-item__label) {
  text-align: left;
  padding: 0;
}

:deep(.el-dialog__body) {
  padding: 0 !important;
  overflow: hidden;
}

:deep(.el-form-item.is-required .el-form-item__label) {
  display: flex;
  align-items: flex-start;
}

:deep(.el-form-item.is-required .el-form-item__label::before) {
  margin-top: 5px;
  line-height: 1.5;
}

.field-label-hint {
  font-size: 10px;
  color: #f56c6c;
  margin-left: 4px;
}

.multi-lang-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.lang-item {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.lang-label {
  white-space: nowrap;
  font-size: 14px;
  color: #606266;
  flex-shrink: 0;
  text-align: left;
}

.lang-item :deep(.el-input) {
  flex: 1;
  min-width: 0;
}

.usage-count-col {
  margin-right: 60px;
}

.basic-inline-item :deep(.el-form-item__content) {
  display: inline-flex;
  align-items: center;
}

.button-badge-color {
  display: flex;
  align-items: center;
  gap: 12px;
}

.badge-color-outer {
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.badge-color-outer.active {
  border: 1px solid #409eff;
  border-radius: 2px;
}

.badge-color-inner {
  width: 20px;
  height: 20px;
  border-radius: 2px;
}

.publisher-summary {
  padding: 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #f5f7fa;
  min-height: 40px;
}

.detail-editor-wrapper {
  position: relative;
  width: 100%;
  height: 300px;
  display: flex;
  flex-direction: column;
}

.detail-lang-tabs {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  display: flex;
  gap: 8px;
}

.lang-tab {
  cursor: pointer;
  font-size: 14px;
  color: #606266;
  padding: 4px 8px;
}

.lang-tab.active {
  color: #409eff;
  font-weight: 500;
  text-decoration: underline;
}

.detail-editor {
  height: 250px;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-weight: 700;
}

.preview-header>span {
  text-align: left;
}

.preview-lang-tabs {
  display: flex;
  gap: 8px;
}

.preview-upload-section {
  width: 100%;
  display: block;
  text-align: center;
}

.preview-upload-section :deep(.el-upload) {
  width: 100%;
}

.preview-upload-section :deep(.el-upload-dragger) {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.preview-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
}

.preview-item {
  position: relative;
  width: 100px;
  height: 160px;
  overflow: hidden;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
}

.preview-upload {
  width: 100%;
  height: 100%;
}

.preview-upload :deep(.el-upload) {
  width: 100%;
  height: 100%;
  border: none;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: fill;
}

.preview-remove {
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0 0 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 14px;
}

.preview-remove:hover {
  background: rgba(0, 0, 0, 0.7);
}

.product-id-link {
  color: #409eff;
  text-decoration: none;
  font-size: 12px;
  white-space: nowrap;
}

.product-id-link:hover {
  text-decoration: underline;
}

.attach-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
}

.attach-tags-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
}

.attach-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 8px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
}

.attach-image-wrapper {
  width: 100px;
  height: 160px;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  overflow: hidden;
}

.attach-image {
  width: 100%;
  height: 100%;
  object-fit: fill;
}

.attach-name-inputs {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.attach-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.attach-name-label {
  width: 40px;
  font-size: 14px;
  color: #606266;
  flex-shrink: 0;
}

.attach-name-input {
  width: 160px;
}

.attach-remove {
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0 0 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 14px;
}

.attach-remove:hover {
  background: rgba(0, 0, 0, 0.7);
}

.footer-buttons {
  text-align: center;
  margin-top: 20px;
  margin-bottom: 20px;
}

.footer-buttons .el-button {
  margin: 0 8px;
}

.row-margin-top {
  margin-top: 10px;
}

.full-width-select {
  width: 100%;
}

.full-width-table {
  width: 100%;
}
</style>
